// services/movimientoCajaWebSocketService.ts

type EventCallback = (data: any) => void;

class MovimientoCajaWebSocketService {
    private ws: WebSocket | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 3000;
    private listeners: Map<string, Set<EventCallback>> = new Map();
    private connectionStatusListeners: Set<(status: boolean) => void> = new Set();
    private isConnected = false;
    private intentionalClose = false; // Para distinguir cierre intencional
    // Sesión POS EXPLÍCITA del canal: el backend no infiere ninguna caja
    // (compartida entre responsables) y cierra el socket sin ``sesion_id``.
    private sessionId: number | null = null;

    getSessionId(): number | null {
        return this.sessionId;
    }

    connect(sesionId?: number | null) {
        if (!Number.isInteger(sesionId) || (sesionId as number) <= 0) {
            console.warn('[MovCajaWebSocket] Se requiere la sesión POS explícita para abrir el canal');
            return;
        }
        // Cambio de sesión: se cierra el canal anterior antes de abrir el nuevo.
        if (this.ws && this.sessionId !== sesionId) {
            this.disconnect();
        }
        // Evitar múltiples conexiones simultáneas
        if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
            console.log('[MovCajaWebSocket] Conexión ya existente, no se crea una nueva');
            return;
        }
        this.sessionId = sesionId as number;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const backendHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '');
        const wsUrl = `${protocol}//${backendHost}/ws/movimientos-caja/?sesion_id=${this.sessionId}`;

        console.log('[MovCajaWebSocket] Conectando a:', wsUrl);
        this.intentionalClose = false;
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
            console.log('[MovCajaWebSocket] ✅ Conectado exitosamente');
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.notifyConnectionStatus(true);
        };

        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('[MovCajaWebSocket] 📨 Mensaje recibido:', data);
                this.notifyListeners('movimiento_caja_actualizado', data);
            } catch (error) {
                console.error('[MovCajaWebSocket] Error parseando mensaje', error);
            }
        };

        this.ws.onclose = (event) => {
            console.log(`[MovCajaWebSocket] 🔌 Desconectado (código: ${event.code})`);
            this.isConnected = false;
            this.notifyConnectionStatus(false);
            this.ws = null;

            // Solo reintentar si no fue un cierre intencional
            if (!this.intentionalClose) {
                this.attemptReconnect();
            }
        };

        this.ws.onerror = (error) => {
            console.error('[MovCajaWebSocket] ❌ Error', error);
            // No cerrar manualmente aquí, onclose se encargará
        };
    }

    private attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('[MovCajaWebSocket] ⚠️ Máximos intentos de reconexión alcanzados');
            return;
        }
        setTimeout(() => {
            if (this.intentionalClose || this.sessionId === null) return;
            console.log(`[MovCajaWebSocket] 🔄 Reconectando intento ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts}`);
            this.reconnectAttempts++;
            this.connect(this.sessionId);
        }, this.reconnectDelay);
    }

    disconnect() {
        this.intentionalClose = true;
        const socket = this.ws;
        this.ws = null;
        this.sessionId = null;
        if (socket) {
            socket.onclose = null;
            socket.onmessage = null;
            socket.onerror = null;
            socket.close();
        }
        this.isConnected = false;
        this.notifyConnectionStatus(false);
        this.reconnectAttempts = 0; // Reiniciar contador
    }

    on(event: string, callback: EventCallback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);
        console.log(`[MovCajaWebSocket] Listener registrado para evento: ${event}`);
        return () => this.off(event, callback);
    }

    off(event: string, callback: EventCallback) {
        this.listeners.get(event)?.delete(callback);
    }

    private notifyListeners(event: string, data: any) {
        const callbacks = this.listeners.get(event);
        console.log(`[MovCajaWebSocket] Notificando ${callbacks?.size || 0} listeners para evento: ${event}`);
        callbacks?.forEach((cb) => cb(data));
    }

    onConnectionStatus(callback: (connected: boolean) => void) {
        this.connectionStatusListeners.add(callback);
        callback(this.isConnected);
        return () => this.connectionStatusListeners.delete(callback);
    }

    private notifyConnectionStatus(connected: boolean) {
        this.connectionStatusListeners.forEach((cb) => cb(connected));
    }

    getConnected(): boolean {
        return this.isConnected;
    }
}

export const movimientoCajaWebSocketService = new MovimientoCajaWebSocketService();